import React, { useState } from "react";

interface CheckboxGroupProps {
  options: Record<string, string> | null | undefined;
  value: string[];
  onChange: (selected: string[] | string) => void;
  label?: string;
  single?: boolean;
  border?: boolean;
  row?: boolean;
}

export const CheckboxGroup: React.FC<CheckboxGroupProps> = ({
  options,
  value,
  onChange,
  label,
  single = false,
  border = true,
  row = false,
}) => {
  if (!options) return null;
  const [hoveredKey, setHoveredKey] = useState<string | null>(null);
  const toggle = (key: string) => {
    if (single) {
      onChange(value.includes(key) ? "" : key);
    } else {
      const isSelected = value.includes(key);
      onChange(isSelected ? value.filter((v) => v !== key) : [...value, key]);
    }
  };

  return (
    <div
      style={
        border
          ? {
              border: "1px solid var(--disabled-c)",
              borderRadius: "10px",
              marginBottom: "8px",
              padding: "4px 8px",
            }
          : {}
      }
    >
      {label && (
        <div style={{ fontWeight: 700, marginBottom: "8px", fontSize: "16px" }}>
          {label}
        </div>
      )}
      <div
        style={{
          display: "flex",
          flexDirection: row ? "row" : "column",
          gap: "4px",
          flexWrap: "wrap",
        }}
      >
        {Object.entries(options).map(([key, optLabel]) => {
          const isChecked = value.includes(key);
          return (
            <label
              onMouseEnter={() => setHoveredKey(key)}
              onMouseLeave={() => setHoveredKey(null)}
              key={key}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "14px",
                padding: "4px 6px",
                borderRadius: "6px",
                cursor: "pointer",
                background:
                  isChecked || hoveredKey === key
                    ? "rgba(24, 96, 217, 0.06)"
                    : "transparent",
                userSelect: "none",
              }}
            >
              <div
                style={{
                  width: "22px",
                  height: "22px",
                  minWidth: "22px",
                  borderRadius: single ? "50%" : "6px",
                  border: `1.5px solid ${isChecked ? "var(--blue)" : "var(--disabled-c)"}`,
                  backgroundColor: isChecked ? "var(--blue)" : "transparent",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  transition: "all 0.15s",
                }}
              >
                {isChecked &&
                  (single ? (
                    // Крапка для radio
                    <div
                      style={{
                        width: "8px",
                        height: "8px",
                        borderRadius: "50%",
                        backgroundColor: "white",
                      }}
                    />
                  ) : (
                    // Галочка для checkbox
                    <svg width="12" height="9" viewBox="0 0 12 9" fill="none">
                      <path
                        d="M1 4L4.5 7.5L11 1"
                        stroke="white"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  ))}
              </div>
              <span style={{ fontSize: "15px", color: "var(--black)" }}>
                {optLabel}
              </span>
              <input
                type={single ? "radio" : "checkbox"}
                checked={isChecked}
                onChange={() => toggle(key)}
                style={{ display: "none" }}
              />
            </label>
          );
        })}
      </div>
    </div>
  );
};

interface CheckboxProps {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  style?: React.CSSProperties;
}

export const CheckboxSingle: React.FC<CheckboxProps> = ({
  label,
  checked,
  onChange,
  style,
}) => {
  return (
    <label
      style={{
        display: "flex",
        alignItems: "center",
        gap: "12px",
        padding: "10px 0",
        cursor: "pointer",
        userSelect: "none",
        width: "100%",
        overflow: "hidden",
      }}
    >
      <div
        onClick={() => onChange(!checked)}
        style={{
          width: "22px",
          height: "22px",
          minWidth: "22px",
          borderRadius: "6px",
          border: `1.5px solid ${checked ? "var(--blue)" : "var(--disabled-c)"}`,
          backgroundColor: checked ? "var(--blue)" : "transparent",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          transition: "all 0.15s",
          flexShrink: 0,
        }}
      >
        {checked && (
          <svg width="12" height="9" viewBox="0 0 12 9" fill="none">
            <path
              d="M1 4L4.5 7.5L11 1"
              stroke="white"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
      </div>
      <span
        style={{
          fontSize: "15px",
          color: "var(--black)",
          fontWeight: 500,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          width: "100%",
          ...style,
        }}
        title={label}
      >
        {label}
      </span>
    </label>
  );
};
